import React, { useState, useEffect, useRef, useMemo } from "react";
import "./styles/customerOrderLayer.css";
import { Link } from "react-router-dom";
import { PlusCircle } from "@phosphor-icons/react";
import apiProvider from "../apiProvider/wholesaleorderapi";
import customerapiProvider from "../apiProvider/customerorderapi";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import InvoiceTemplate from "./InvoiceTemplate";
import { Icon } from '@iconify/react';
import ReactTableComponent from "../table/ReactTableComponent";

function CustomerOrderLayer() {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [downloadingOrder, setDownloadingOrder] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const invoiceTemplateRef = useRef(null);
  const [activeTab, setActiveTab] = useState("pending");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState([]); // Added to satisfy ReactTableComponent props

  const [tabCounts, setTabCounts] = useState({
    pending: 0,
    packed: 0,
    shipped: 0,
    delivered: 0,
    "return-initiated": 0,
    cancelled: 0,
  });
  const [initialLoad, setInitialLoad] = useState(true);

  const tabs = [
    { key: "pending", label: "Pending" },
    { key: "packed", label: "Packed" },
    { key: "shipped", label: "Shipped" },
    { key: "delivered", label: "Delivered" },
    // { key: "return-initiated", label: "Return-initiated" },
    // { key: "cancelled", label: "Cancelled" },
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      await fetchTabCounts();
      await fetchData();
      setInitialLoad(false);
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!initialLoad) {
      fetchData();
    }
  }, [page, limit, activeTab]);

  const fetchTabCounts = async () => {
    try {
      const counts = {};
      for (const tab of tabs) {
        const input = {
          page: 0,
          limit: 1,
          type: "customer",
          status: tab.key,
        };
        const result = await apiProvider.getWholesaleOrder(input);
        counts[tab.key] = result?.response?.total || 0;
      }
      setTabCounts(counts);
    } catch (error) {
      console.error("Error fetching tab counts:", error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      let input = {
        page: page,
        limit: limit,
        type: "customer",
        status: activeTab,
      };
      const result = await apiProvider.getWholesaleOrder(input);

      if (result?.status && result?.response?.data) {
        console.log(result.response.data, "result.response.data");

        const transformedOrders = result.response.data.map((order) => ({
          id: order.orderCode,
          _id: order._id,
          name: order.name || "N/A",
          date: formatDate(order.createdAt),
          amount: order.totalAmount || "0",
          type: order.paymentMode || "COD",
          originalData: order,
        }));

        setOrders(transformedOrders);
        const totalRecords = result.response.total || result.response.data.length;
        setTotal(totalRecords);
        setTotalPages(Math.ceil(totalRecords / limit));

        setTabCounts(prev => ({
          ...prev,
          [activeTab]: result.response.total
        }));
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDownload = async (orderId) => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const result = await customerapiProvider.orderDetails(orderId);
      if (result?.status) {
        setDownloadingOrder(result.response.data);
      } else {
        console.error("Failed to fetch order details for download");
        setIsDownloading(false);
      }
    } catch (error) {
      console.error("API Error:", error);
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (downloadingOrder && invoiceTemplateRef.current) {
      html2canvas(invoiceTemplateRef.current, {
        useCORS: true,
        scale: 2,
      })
        .then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF("p", "mm", "a4");
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
          const imgX = (pdfWidth - imgWidth * ratio) / 2;
          const imgY = 0;
          pdf.addImage(
            imgData,
            "PNG",
            imgX,
            imgY,
            imgWidth * ratio,
            imgHeight * ratio
          );
          pdf.save(`invoice-${downloadingOrder.orderCode}.pdf`);
        })
        .catch((err) => {
          console.error("Error generating PDF:", err);
        })
        .finally(() => {
          setDownloadingOrder(null); // Reset after download
          setIsDownloading(false);
        });
    }
  }, [downloadingOrder]);

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage((prev) => prev - 1);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'S.No',
        cell: info => info.row.index + 1 + (page * limit),
      },
      {
        header: 'Order ID',
        accessorKey: 'id',
      },
      {
        header: 'Customer Name',
        accessorKey: 'name',
      },
      {
        header: 'Order Date & Time',
        accessorKey: 'date',
      },
      {
        header: 'Total Amount',
        accessorKey: 'amount',
        cell: info => {
          const order = info.row.original.originalData;
          const currencySymbol = order?.currency === 'SGD' ? 'S$' : '₹';
          return `${currencySymbol}${info.getValue()}`;
        }
      },
      {
        header: 'Payment Mode',
        accessorKey: 'type',
        cell: info => {
          const order = info.row.original.originalData;
          return `${info.getValue()} ${order?.currency ? `(${order.currency})` : '(INR)'}`;
        }
      },
      {
        header: 'Action',
        cell: info => {
          const order = info.row.original;
          return (
            <div className="dropdown" style={{ cursor: "pointer" }}>
              <Icon
                icon="entypo:dots-three-vertical"
                className="menu-icon"
                data-bs-toggle="dropdown"
                data-bs-boundary="viewport"
                data-bs-popper-config='{"strategy":"fixed"}'
                aria-expanded="false"
              />
              <div className="dropdown-menu" style={{ zIndex: 9999 }}>
                <Link
                  to={`/order-invoice-details-customer/${order._id}`}
                  className="dropdown-item"
                >
                  View
                </Link>
                <a className="dropdown-item" onClick={() => handleDownload(order._id)}
                  disabled={isDownloading}>
                  {isDownloading ? "Downloading..." : "Download Invoice"}
                </a>
              </div>
            </div>
          );
        },
      }
    ],
    [page, limit, isDownloading]
  );

  // Columns for validation/filtering if needed by ReactTableComponent
  const orderFilters = ["id", "name", "date", "amount", "type"];

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-xl-12">
          <div className="cards">
            <div className="card-body">
              <div className="col text-end mb-3">
                <a href="create-order">
                </a>
              </div>

              <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                {downloadingOrder && (
                  <InvoiceTemplate
                    ref={invoiceTemplateRef}
                    orderData={downloadingOrder}
                    paymentStatus={downloadingOrder.paymentStatus}
                  />
                )}
              </div>

              {/* Tabs */}
              <ul className="nav nav-pills red-tabs nav-justified" role="tablist">
                {tabs.map((tab) => (
                  <li className="nav-item" key={tab.key}>
                    <button
                      className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab(tab.key);
                        setPage(0);
                      }}
                    >
                      <span className="d-none d-sm-block">{tab.label}</span>
                      <span className="badgde bg-dangder ms-1">
                        {tabCounts[tab.key] || 0}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              {/* Tab Content */}
              <div className="tab-content p-3 text-muted">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex flex-wrap align-items-center mb-3">
                      <h5 className="card-title me-2">
                        {tabs.find((t) => t.key === activeTab)?.label} List
                      </h5>
                      {loading && (
                        <div className="spinner-border spinner-border-sm ms-2" role="status"></div>
                      )}
                    </div>

                    <div className="table-responsives">
                      <ReactTableComponent
                        data={orders}
                        columns={columns}
                        filterableColumns={orderFilters}
                        pageIndex={page}
                        totalPages={totalPages}
                        totalRecords={total}
                        onNextPage={handleNextPage}
                        onPreviousPage={handlePreviousPage}
                        filters={filters}
                        setFilters={setFilters}
                      />

                      {/* Limit Selector - Kept if ReactTableComponent doesn't have it built-in or if we want it separate */}
                      <div className="d-flex align-items-center mt-3 justify-content-end gap-2">
                        <span>Items per page:</span>
                        <select
                          className="form-select w-auto"
                          value={limit}
                          onChange={(e) => {
                            setLimit(Number(e.target.value));
                            setPage(0);
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerOrderLayer;